import React, { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Badge,
  Spinner,
  Input,
  Label,
  Button,
} from "reactstrap";
import { PlusCircle, Filter } from "react-feather";
import { Range, getTrackBackground } from "react-range";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { Breadcrumbs } from "../../AbstractElements";
import { titleCase } from "../../utils/case";
import { useTranslation } from "react-i18next";
import { getLots, getLotSelectionData } from "../../api/lot.api";
import { toast } from "react-toastify";
import { hasRole } from "../../utils/auth";

const LotCard = ({ item, onClick }) => {
  const { t } = useTranslation();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiryDate = item.expiredAt
    ? new Date(`${item.expiredAt}T00:00:00`)
    : null;

  const isExpired = expiryDate ? expiryDate < today : false;

  return (
    <Col xxl="3" xl="3" lg="4" md="6" className="mb-4">
      <div
        className="project-box"
        onClick={onClick}
        style={{
          cursor: "pointer",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "200px",
            margin: "-20px -20px 15px -20px",
            background: "#f5f6f9",
          }}
        >
          <img
            src={
              item.photos?.length
                ? `${process.env.REACT_APP_FILES_URL}/${item.photos[0]}`
                : "https://www.magnific.com/free-photos-vectors/camp-illustration"
            }
            alt={item.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </div>

        <div className="d-flex align-items-center gap-2">
          <Badge
            color={
              isExpired
                ? "danger"
                : item.status === "active"
                ? "success"
                : "dark"
            }
          >
            {isExpired
              ? titleCase(t("expired"))
              : titleCase(t(item.status || "inactive"))}
          </Badge>

          <Badge
            color="secondary"
          >
            {item.isEvent
              ? titleCase(t("event"))
              : item.isDayTrip
              ? titleCase(t("day trip"))
              : titleCase(t("standard"))}
          </Badge>
        </div>

        <h6 className="mt-3">{item.name}</h6>

        <div className="mb-2">
          {titleCase(t(item.type || "permanent"))}
        </div>

        {expiryDate && (
          <div className={`small ${isExpired ? "text-danger" : "text-muted"}`}>
            {titleCase(t("expiry date"))}:{" "}
            {expiryDate.toLocaleDateString("en-GB")}
          </div>
        )}
      </div>
    </Col>
  );
};

const LotMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const observerRef = useRef(null);
  const canManageLots = hasRole(
    "superadmin",
    "finance"
  );
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [selectionData, setSelectionData] = useState({
    locations: [],
    addOns: [],
  });
  const [showFilters, setShowFilters] = useState(false);

  const defaultQuery = {
    page: 1,
    perPage: 12,
    sortField: "location",
    sortOrder: "ASC",
    keyword: "",
    location: "",
    type: "",
    status: "",
    requiresPayment: null,
    requiresApproval: null,
    allowPublicBooking: null,
    minCapacity: 0,
    maxCapacity: 100000,
  };

  const defaultFilters = {
    keyword: "",
    location: "",
    type: "",
    status: "",
    requiresPayment: null,
    requiresApproval: null,
    allowPublicBooking: null,
    minCapacity: 0,
    maxCapacity: 100000,
  };

  const [query, setQuery] = useState(defaultQuery);
  const [filters, setFilters] = useState(defaultFilters);

  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    perPage: 12,
    totalPages: 1,
  });

  const hasMore = meta.page < meta.totalPages;

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTriStateCheckbox = (field) => {
    setFilters((prev) => ({
      ...prev,
      [field]: prev[field] === true ? null : true,
    }));
  };

  const handleApplyFilters = () => {
    setLots([]);
    setInitialLoaded(false);

    setQuery((prev) => ({
      ...prev,
      page: 1,
      ...filters,
    }));
  };

  const handleResetFilters = () => {
    setLots([]);
    setInitialLoaded(false);
    setFilters(defaultFilters);
    setQuery(defaultQuery);
  };

  const fetchLots = async () => {
    try {
      setLoading(true);

      const res = await getLots(query);
      const newLots = res?.data || [];

      setLots((prev) =>
        query.page === 1 ? newLots : [...prev, ...newLots]
      );

      setMeta(
        res?.meta || {
          total: 0,
          page: query.page,
          perPage: query.perPage,
          totalPages: 1,
        }
      );

      setInitialLoaded(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSelectionData = async () => {
    try {
      const data = await getLotSelectionData();

      setSelectionData({
        locations: data.locations || [],
        addOns: data.addOns || [],
      });
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    loadSelectionData();
  }, []);

  useEffect(() => {
    fetchLots();
  }, [query]);

  useEffect(() => {
    if (!observerRef.current || loading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];

        if (firstEntry.isIntersecting && !loading && hasMore) {
          setQuery((prev) => ({
            ...prev,
            page: prev.page + 1,
          }));
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      }
    );

    observer.observe(observerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [loading, hasMore]);

  const groupedLots = lots.reduce((groups, lot) => {
    const location = lot.location || titleCase(t("unassigned location"));

    if (!groups[location]) {
      groups[location] = [];
    }

    groups[location].push(lot);

    return groups;
  }, {});

  const locationGroups = Object.entries(groupedLots);

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("lot"))}
        title={titleCase(t("lot management"))}
        mainTitle={titleCase(t("lot management"))}
      />

      <Container fluid>
        <Row className="project-card">
          <Col md="12" className="project-list">
            <Card>
              <Row className="align-items-center">
                <Col md="6">
                  <h5 className="mb-0">{titleCase(t("lot list"))}</h5>
                </Col>

                <Col md="6">
                  <div className="d-flex justify-content-end gap-2">
                    <Button color="light" onClick={() => setShowFilters(!showFilters)}>
                      {showFilters
                        ? t("hide filters").toUpperCase()
                        : t("show filters").toUpperCase()}
                    </Button>
                    {canManageLots && (
                      <Button
                        color="success"
                        onClick={() => navigate("/lots/create")}
                      >
                        {t("create").toUpperCase()}
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col sm="12">
            {showFilters && (
            <Card className="mb-3">
              <CardBody>
                <Row className="align-items-end">
                  <Col md="6">
                    <Label>{titleCase(t("search"))}</Label>
                    <Input
                      type="text"
                      value={filters.keyword}
                      placeholder={titleCase(t("search lot"))}
                      onChange={(e) =>
                        handleFilterChange("keyword", e.target.value)
                      }
                    />
                  </Col>

                  <Col md="6">
                    <Label>{titleCase(t("location"))}</Label>

                    <Typeahead
                      id="lot-location-filter"
                      allowNew
                      clearButton
                      multiple={false}
                      options={selectionData.locations}
                      selected={filters.location ? [filters.location] : []}
                      placeholder={titleCase(t("enter or select location"))}
                      newSelectionPrefix={`${titleCase(t("add new location"))}: `}
                      onChange={(selected) => {
                        const value =
                          selected.length > 0
                            ? typeof selected[0] === "string"
                              ? selected[0]
                              : selected[0].label
                            : "";

                        handleFilterChange("location", value);
                      }}
                      onInputChange={(text) =>
                        handleFilterChange("location", text)
                      }
                    />
                  </Col>
                </Row>

                <Row className="mt-3 align-items-end">
                  <Col md="3">
                    <Label>{titleCase(t("type"))}</Label>

                    <Input
                      type="select"
                      value={filters.type}
                      onChange={(e) =>
                        handleFilterChange("type", e.target.value)
                      }
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      <option value="permanent">
                        {titleCase(t("permanent"))}
                      </option>
                      <option value="temporary">
                        {titleCase(t("temporary"))}
                      </option>
                    </Input>
                  </Col>

                  <Col md="3">
                    <Label>{titleCase(t("status"))}</Label>

                    <Input
                      type="select"
                      value={filters.status}
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                    >
                      <option value="">{titleCase(t("all"))}</option>
                      <option value="active">
                        {titleCase(t("active"))}
                      </option>
                      <option value="inactive">
                        {titleCase(t("inactive"))}
                      </option>
                    </Input>
                  </Col>

                  <Col md="6">
                    <Label>{titleCase(t("capacity"))}</Label>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <Range
                        values={[
                          Number(filters.minCapacity || 0),
                          Number(filters.maxCapacity || 20),
                        ]}
                        step={1}
                        min={0}
                        max={20}
                        onChange={(values) => {
                          setFilters((prev) => ({
                            ...prev,
                            minCapacity: values[0],
                            maxCapacity: values[1],
                          }));
                        }}
                        renderTrack={({ props, children }) => (
                          <div
                            onMouseDown={props.onMouseDown}
                            onTouchStart={props.onTouchStart}
                            style={{
                              ...props.style,
                              height: "36px",
                              display: "flex",
                              width: "100%",
                            }}
                          >
                            <output style={{ marginTop: "12px" }}>0</output>

                            <div
                              ref={props.ref}
                              style={{
                                height: "5px",
                                width: "100%",
                                borderRadius: "4px",
                                background: getTrackBackground({
                                  values: [
                                    Number(filters.minCapacity || 0),
                                    Number(filters.maxCapacity || 20),
                                  ],
                                  colors: ["#ccc", "#7366ff", "#ccc"],
                                  min: 0,
                                  max: 20,
                                }),
                                alignSelf: "center",
                                margin: "0 10px",
                              }}
                            >
                              {children}
                            </div>

                            <output style={{ marginTop: "12px" }}>20</output>
                          </div>
                        )}
                        renderThumb={({ props, isDragged }) => (
                          <div
                            {...props}
                            style={{
                              ...props.style,
                              height: "35px",
                              width: "35px",
                              borderRadius: "30px",
                              backgroundColor: "#FFF",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              boxShadow: "0px 2px 6px #AAA",
                            }}
                          >
                            <div
                              style={{
                                height: "16px",
                                width: "5px",
                                backgroundColor: isDragged
                                  ? "#7366ff"
                                  : "#CCC",
                              }}
                            />
                          </div>
                        )}
                      />
                    </div>
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col md="2">
                    <div className="checkbox checkbox-primary">
                      <Input
                        id="requires-payment-filter"
                        type="checkbox"
                        checked={filters.requiresPayment}
                        onChange={(e) =>
                          handleFilterChange(
                            "requiresPayment",
                            e.target.checked
                          )
                        }
                      />
                      <Label for="requires-payment-filter">
                        {titleCase(t("requires payment"))}
                      </Label>
                    </div>
                  </Col>

                  <Col md="2">
                    <div className="checkbox checkbox-primary">
                      <Input
                        id="requires-approval-filter"
                        type="checkbox"
                        checked={filters.requiresApproval}
                        onChange={(e) =>
                          handleFilterChange(
                            "requiresApproval",
                            e.target.checked
                          )
                        }
                      />
                      <Label for="requires-approval-filter">
                        {titleCase(t("requires approval"))}
                      </Label>
                    </div>
                  </Col>

                  <Col md="2">
                    <div className="checkbox checkbox-primary">
                      <Input
                        id="allow-public-booking-filter"
                        type="checkbox"
                        checked={filters.allowPublicBooking}
                        onChange={(e) =>
                          handleFilterChange(
                            "allowPublicBooking",
                            e.target.checked
                          )
                        }
                      />
                      <Label for="allow-public-booking-filter">
                        {titleCase(t("allow public booking"))}
                      </Label>
                    </div>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col md="6">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleApplyFilters}
                    >
                      {titleCase(t("apply filters"))}
                    </button>
                  </Col>

                  <Col md="6">
                    <button
                      type="button"
                      className="btn btn-light w-100"
                      onClick={handleResetFilters}
                    >
                      {titleCase(t("reset filters"))}
                    </button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
            )}

            <Card>
              <CardBody>
                {locationGroups.length > 0 ? (
                  locationGroups.map(([location, items]) => (
                    <div key={location} className="mb-5">
                      <h5 className="mb-3 border-bottom pb-2">
                        {location}
                      </h5>

                      <Row>
                        {items.map((item) => (
                          <LotCard
                            key={item.id}
                            item={item}
                            onClick={() =>
                              navigate(`/lots/view/${item.id}`)
                            }
                          />
                        ))}
                      </Row>
                    </div>
                  ))
                ) : initialLoaded ? (
                  <p className="text-muted mb-0">
                    {titleCase(t("no lots found"))}
                  </p>
                ) : null}

                <div
                  ref={observerRef}
                  className="d-flex justify-content-center py-4"
                >
                  {loading && (
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <Spinner size="sm" />
                      <span>{titleCase(t("loading"))}...</span>
                    </div>
                  )}

                  {!loading && initialLoaded && !hasMore && lots.length > 0 && (
                    <span className="text-muted">
                      {titleCase(t("all lots loaded"))}
                    </span>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default LotMain;